/**
 * Module Block Editor - Rich Content Block Management
 * Supports drag-and-drop reordering and multiple block types
 */
import React, { useState, useEffect } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';
const COURSES_API = `${API_BASE}/courses`;

// Quiz Editor Sub-Component
function QuizEditor({ blockId }) {
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [title, setTitle] = useState('');
  const [passMark, setPassMark] = useState(70);
  const [maxAttempts, setMaxAttempts] = useState(1);
  const [randomize, setRandomize] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkForExistingQuiz();
  }, [blockId]);

  const checkForExistingQuiz = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');

      // Try to create quiz - if it already exists, this will fail with 400
      // This is a workaround since we don't have a GET by module_block_id endpoint
      const createResponse = await fetch(`${API_BASE}/quizzes/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'Untitled Quiz',
          pass_mark: 70,
          max_attempts: 1,
          randomize_questions: false
        })
      });

      if (createResponse.ok) {
        // Quiz was just created
        const newQuiz = await createResponse.json();
        await fetchQuizById(newQuiz.id);
      } else if (createResponse.status === 400) {
        // Quiz already exists, but we need to get its ID
        // We'll handle this by showing the "no quiz" state for now
        // In production, you'd want an endpoint: GET /module-blocks/{id}/quiz
        setLoading(false);
      } else {
        throw new Error('Failed to check for quiz');
      }
    } catch (err) {
      console.error('Error checking quiz:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchQuizById = async (quizId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/quizzes/${quizId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch quiz');

      const data = await response.json();
      setQuiz(data);
      setTitle(data.title || '');
      setPassMark(data.pass_mark || 70);
      setMaxAttempts(data.max_attempts || 1);
      setRandomize(data.randomize_questions || false);
      setQuestions(data.questions || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching quiz:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCreateQuiz = async () => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/quizzes/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title || 'Untitled Quiz',
          pass_mark: passMark,
          max_attempts: maxAttempts,
          randomize_questions: randomize
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create quiz');
      }

      const newQuiz = await response.json();
      await fetchQuizById(newQuiz.id);
    } catch (err) {
      setError(err.message || 'Failed to create quiz');
    }
  };

  const handleAddQuestion = async () => {
    if (!quiz) return;

    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/quizzes/${quiz.id}/questions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          question_text: 'New Question',
          question_type: 'mcq',
          required: true
        })
      });

      if (!response.ok) throw new Error('Failed to add question');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to add question');
    }
  };

  const handleUpdateQuestion = async (questionId, updates) => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/questions/${questionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update question');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to update question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!confirm('Delete this question?')) return;

    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/questions/${questionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete question');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to delete question');
    }
  };

  const handleAddOption = async (questionId) => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/questions/${questionId}/options`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          option_text: 'New Option',
          is_correct: false
        })
      });

      if (!response.ok) throw new Error('Failed to add option');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to add option');
    }
  };

  const handleUpdateOption = async (optionId, updates) => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/options/${optionId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) throw new Error('Failed to update option');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to update option');
    }
  };

  const handleDeleteOption = async (optionId) => {
    if (!confirm('Delete this option?')) return;

    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/options/${optionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete option');
      await fetchQuizById(quiz.id);
    } catch (err) {
      setError(err.message || 'Failed to delete option');
    }
  };

  if (loading) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>Loading quiz...</div>;
  }

  if (!quiz) {
    return (
      <div style={{ padding: '24px', background: '#f9f9f9', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '16px', color: '#d4af37', fontSize: '1.2rem' }}>Create Quiz</h3>

        {error && (
          <div style={{ padding: '12px', marginBottom: '16px', background: '#ffebee', border: '1px solid #e74c3c', borderRadius: '4px', color: '#c62828', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Quiz Title *
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
            placeholder="Enter quiz title"
          />
        </label>
        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Pass Mark (%)
          <input
            type="number"
            value={passMark}
            onChange={(e) => setPassMark(parseInt(e.target.value))}
            min="0"
            max="100"
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
          />
        </label>
        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Max Attempts
          <input
            type="number"
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(parseInt(e.target.value))}
            min="1"
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
          />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={randomize}
            onChange={(e) => setRandomize(e.target.checked)}
            style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
          />
          Randomize Questions
        </label>
        <button
          onClick={handleCreateQuiz}
          style={{ padding: '12px 24px', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', width: '100%' }}
        >
          Create Quiz
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#d4af37', fontSize: '1.2rem' }}>{quiz.title}</h3>
        <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
          <div>Pass Mark: <strong>{quiz.pass_mark}%</strong></div>
          <div>Max Attempts: <strong>{quiz.max_attempts}</strong></div>
          <div>Randomize Questions: <strong>{quiz.randomize_questions ? 'Yes' : 'No'}</strong></div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', marginBottom: '16px', background: '#ffebee', border: '1px solid #e74c3c', borderRadius: '4px', color: '#c62828', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={handleAddQuestion}
          style={{ padding: '10px 20px', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem' }}
        >
          + Add Question
        </button>
      </div>

      {questions.map((question, qIndex) => (
        <div key={question.id} style={{ border: '2px solid #d4af37', borderRadius: '4px', padding: '16px', marginBottom: '16px', background: '#f9f9f9' }}>
          <div style={{ marginBottom: '12px', fontWeight: '700', color: '#1a1a1a' }}>Question {qIndex + 1}</div>

          <input
            type="text"
            value={question.question_text}
            onChange={(e) => handleUpdateQuestion(question.id, { question_text: e.target.value })}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '8px' }}
          />

          <select
            value={question.question_type}
            onChange={(e) => handleUpdateQuestion(question.id, { question_type: e.target.value })}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '12px' }}
          >
            <option value="mcq">Multiple Choice (Single Answer)</option>
            <option value="multi_select">Multiple Choice (Multiple Answers)</option>
            <option value="true_false">True/False</option>
          </select>

          <div style={{ marginBottom: '8px', fontWeight: '600', fontSize: '0.9rem' }}>Options:</div>
          {question.options && question.options.map((option) => (
            <div key={option.id} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={option.is_correct}
                onChange={(e) => handleUpdateOption(option.id, { is_correct: e.target.checked })}
                title="Mark as correct"
              />
              <input
                type="text"
                value={option.option_text}
                onChange={(e) => handleUpdateOption(option.id, { option_text: e.target.value })}
                style={{ flex: 1, padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
              />
              <button
                onClick={() => handleDeleteOption(option.id)}
                style={{ padding: '4px 8px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
              >
                Delete
              </button>
            </div>
          ))}

          <button
            onClick={() => handleAddOption(question.id)}
            style={{ padding: '6px 12px', background: '#fff', border: '2px solid #d4af37', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', marginRight: '8px' }}
          >
            + Add Option
          </button>

          <button
            onClick={() => handleDeleteQuestion(question.id)}
            style={{ padding: '6px 12px', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' }}
          >
            Delete Question
          </button>
        </div>
      ))}

      {questions.length === 0 && (
        <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
          No questions yet. Click "Add Question" to get started.
        </div>
      )}
    </div>
  );
}

// Assignment Editor Sub-Component
function AssignmentEditor({ blockId }) {
  const [assignment, setAssignment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [allowedFileTypes, setAllowedFileTypes] = useState('.pdf,.docx,.doc');
  const [maxFileSize, setMaxFileSize] = useState(10485760); // 10MB
  const [required, setRequired] = useState(true);

  useEffect(() => {
    checkForExistingAssignment();
  }, [blockId]);

  const checkForExistingAssignment = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');

      // Try to create assignment - if it already exists, this will fail with 400
      const createResponse = await fetch(`${API_BASE}/assignments/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: 'Untitled Assignment',
          instructions: 'Upload your completed assignment file.',
          allowed_file_types: ['.pdf', '.docx', '.doc'],
          max_file_size: 10485760,
          required: true
        })
      });

      if (createResponse.ok) {
        // Assignment was just created
        const newAssignment = await createResponse.json();
        await fetchAssignmentById(newAssignment.id);
      } else if (createResponse.status === 400) {
        // Assignment already exists - we need to fetch it
        // For now show the "no assignment" state
        setLoading(false);
      } else {
        throw new Error('Failed to check for assignment');
      }
    } catch (err) {
      console.error('Error checking assignment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchAssignmentById = async (assignmentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/assignments/${assignmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch assignment');

      const data = await response.json();
      setAssignment(data);
      setTitle(data.title || '');
      setInstructions(data.instructions || '');
      setAllowedFileTypes(data.allowed_file_types ? data.allowed_file_types.join(',') : '');
      setMaxFileSize(data.max_file_size || 10485760);
      setRequired(data.required !== undefined ? data.required : true);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching assignment:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCreateAssignment = async () => {
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/assignments/${blockId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title || 'Untitled Assignment',
          instructions: instructions || 'Upload your completed assignment file.',
          allowed_file_types: allowedFileTypes.split(',').map(t => t.trim()),
          max_file_size: maxFileSize,
          required: required
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create assignment');
      }

      const newAssignment = await response.json();
      await fetchAssignmentById(newAssignment.id);
    } catch (err) {
      setError(err.message || 'Failed to create assignment');
    }
  };

  const handleUpdateAssignment = async () => {
    if (!assignment) return;

    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/assignments/${assignment.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: title,
          instructions: instructions,
          allowed_file_types: allowedFileTypes.split(',').map(t => t.trim()),
          max_file_size: maxFileSize,
          required: required
        })
      });

      if (!response.ok) throw new Error('Failed to update assignment');

      alert('Assignment updated successfully!');
    } catch (err) {
      setError(err.message || 'Failed to update assignment');
    }
  };

  if (loading) {
    return <div style={{ padding: '24px', textAlign: 'center', color: '#666' }}>Loading assignment...</div>;
  }

  if (!assignment) {
    return (
      <div style={{ padding: '24px', background: '#f9f9f9', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '16px', color: '#d4af37', fontSize: '1.2rem' }}>Create Assignment</h3>

        {error && (
          <div style={{ padding: '12px', marginBottom: '16px', background: '#ffebee', border: '1px solid #e74c3c', borderRadius: '4px', color: '#c62828', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Assignment Title *
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
            placeholder="Enter assignment title"
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Instructions *
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem', minHeight: '100px' }}
            placeholder="Enter assignment instructions"
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Allowed File Types (comma-separated)
          <input
            type="text"
            value={allowedFileTypes}
            onChange={(e) => setAllowedFileTypes(e.target.value)}
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
            placeholder=".pdf,.docx,.doc"
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Max File Size (bytes)
          <input
            type="number"
            value={maxFileSize}
            onChange={(e) => setMaxFileSize(parseInt(e.target.value))}
            min="1"
            style={{ width: '100%', padding: '10px', border: '2px solid #d4af37', borderRadius: '4px', marginTop: '6px', fontSize: '0.95rem' }}
          />
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
            {(maxFileSize / 1048576).toFixed(2)} MB
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
          />
          Required for module completion
        </label>

        <button
          onClick={handleCreateAssignment}
          style={{ padding: '12px 24px', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem', width: '100%' }}
        >
          Create Assignment
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#d4af37', fontSize: '1.2rem' }}>{assignment.title}</h3>
        <div style={{ fontSize: '0.9rem', color: '#666', lineHeight: '1.6' }}>
          <div>Allowed Types: <strong>{assignment.allowed_file_types.join(', ')}</strong></div>
          <div>Max Size: <strong>{(assignment.max_file_size / 1048576).toFixed(2)} MB</strong></div>
          <div>Required: <strong>{assignment.required ? 'Yes' : 'No'}</strong></div>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', marginBottom: '16px', background: '#ffebee', border: '1px solid #e74c3c', borderRadius: '4px', color: '#c62828', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      <div style={{ border: '2px solid #d4af37', borderRadius: '4px', padding: '16px', marginBottom: '16px', background: '#f9f9f9' }}>
        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Assignment Title
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '6px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Instructions
          <textarea
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '6px', minHeight: '100px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Allowed File Types (comma-separated)
          <input
            type="text"
            value={allowedFileTypes}
            onChange={(e) => setAllowedFileTypes(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '6px' }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px', fontWeight: '600', fontSize: '0.95rem' }}>
          Max File Size (bytes)
          <input
            type="number"
            value={maxFileSize}
            onChange={(e) => setMaxFileSize(parseInt(e.target.value))}
            min="1"
            style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '6px' }}
          />
          <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
            {(maxFileSize / 1048576).toFixed(2)} MB
          </div>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', marginBottom: '16px', fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={required}
            onChange={(e) => setRequired(e.target.checked)}
            style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
          />
          Required for module completion
        </label>

        <button
          onClick={handleUpdateAssignment}
          style={{ padding: '10px 20px', background: '#d4af37', color: '#1a1a1a', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '700', fontSize: '0.95rem', width: '100%' }}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}

function ModuleBlockEditor({ moduleId }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (moduleId) {
      fetchBlocks();
    }
  }, [moduleId]);

  const fetchBlocks = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${COURSES_API}/modules/${moduleId}/blocks`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch blocks');
      }

      const data = await response.json();
      setBlocks(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setError(err.message || 'Failed to load blocks');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBlock = async (blockType) => {
    try {
      const token = localStorage.getItem('token');

      // Default data for each block type
      const defaultData = {
        text: { content: '' },
        image: { url: '', caption: '', alt: '' },
        video: { url: '', provider: 'youtube', autoplay: false },
        file: { url: '', filename: '' },
        section: { title: '' },
        quiz: { questions: [] },
        audio: { url: '', transcript: '' }
      };

      const response = await fetch(`${COURSES_API}/modules/${moduleId}/blocks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          block_type: blockType,
          data: defaultData[blockType] || {}
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create block');
      }

      await fetchBlocks();
    } catch (err) {
      console.error('Error adding block:', err);
      alert(err.message || 'Failed to add block');
    }
  };

  const handleSaveBlock = async (blockId, data) => {
    setSaving(prev => ({ ...prev, [blockId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${COURSES_API}/modules/${moduleId}/blocks/${blockId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ data })
      });

      if (!response.ok) {
        throw new Error('Failed to save block');
      }

      await fetchBlocks();
      alert('Block saved successfully!');
    } catch (err) {
      console.error('Error saving block:', err);
      alert(err.message || 'Failed to save block');
    } finally {
      setSaving(prev => ({ ...prev, [blockId]: false }));
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!confirm('Are you sure you want to delete this block?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${COURSES_API}/modules/${moduleId}/blocks/${blockId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete block');
      }

      await fetchBlocks();
    } catch (err) {
      console.error('Error deleting block:', err);
      alert(err.message || 'Failed to delete block');
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newBlocks = [...blocks];
    const draggedBlock = newBlocks[draggedIndex];
    newBlocks.splice(draggedIndex, 1);
    newBlocks.splice(index, 0, draggedBlock);

    setBlocks(newBlocks);
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (draggedIndex === null) return;

    try {
      const token = localStorage.getItem('token');
      const reorderedBlocks = blocks.map((block, index) => ({
        id: block.id,
        order_index: index
      }));

      const response = await fetch(`${COURSES_API}/modules/${moduleId}/blocks/reorder`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(reorderedBlocks)
      });

      if (!response.ok) {
        throw new Error('Failed to reorder blocks');
      }

      await fetchBlocks();
    } catch (err) {
      console.error('Error reordering blocks:', err);
      alert(err.message || 'Failed to reorder blocks');
      await fetchBlocks(); // Reload to restore original order
    } finally {
      setDraggedIndex(null);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/courses/upload-media`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const data = await response.json();
      return data.url || data.file_url || data.path;
    } catch (err) {
      console.error('Error uploading file:', err);
      alert(err.message || 'Failed to upload file');
      return null;
    }
  };

  // ============================================================================
  // BLOCK TYPE EDITOR COMPONENTS
  // Each component has its own useState hook at the top level (no violations)
  // ============================================================================

  const TextBlockEditor = ({ block, saving, handleSaveBlock, labelStyle, textareaStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    return (
      <div>
        <label style={labelStyle}>Content</label>
        <textarea
          value={localData.content || ''}
          onChange={(e) => updateLocalData('content', e.target.value)}
          style={textareaStyle}
          rows={6}
          placeholder="Enter your text content here..."
        />
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save Text'}
        </button>
      </div>
    );
  };

  const ImageBlockEditor = ({ block, saving, handleSaveBlock, handleFileUpload, labelStyle, inputStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    const handleFileInputChange = async (e, key) => {
      const file = e.target.files[0];
      if (!file) return;

      const url = await handleFileUpload(file);
      if (url) {
        updateLocalData(key, url);
        if (key === 'url' && !localData.filename) {
          updateLocalData('filename', file.name);
        }
      }
    };

    return (
      <div>
        <label style={labelStyle}>Image Upload</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => handleFileInputChange(e, 'url')}
          style={inputStyle}
        />
        {localData.url && (
          <img src={localData.url} alt="Preview" style={{ maxWidth: '100%', marginTop: '8px', borderRadius: '4px' }} />
        )}
        <label style={{ ...labelStyle, marginTop: '16px' }}>Caption</label>
        <input
          type="text"
          value={localData.caption || ''}
          onChange={(e) => updateLocalData('caption', e.target.value)}
          style={inputStyle}
          placeholder="Image caption"
        />
        <label style={labelStyle}>Alt Text</label>
        <input
          type="text"
          value={localData.alt || ''}
          onChange={(e) => updateLocalData('alt', e.target.value)}
          style={inputStyle}
          placeholder="Alt text for accessibility"
        />
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save Image'}
        </button>
      </div>
    );
  };

  const VideoBlockEditor = ({ block, saving, handleSaveBlock, labelStyle, inputStyle, selectStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    return (
      <div>
        <label style={labelStyle}>Video URL</label>
        <input
          type="text"
          value={localData.url || ''}
          onChange={(e) => updateLocalData('url', e.target.value)}
          style={inputStyle}
          placeholder="https://youtube.com/watch?v=..."
        />
        <label style={labelStyle}>Provider</label>
        <select
          value={localData.provider || 'youtube'}
          onChange={(e) => updateLocalData('provider', e.target.value)}
          style={selectStyle}
        >
          <option value="youtube">YouTube</option>
          <option value="vimeo">Vimeo</option>
          <option value="mp4">MP4 (Direct Link)</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', marginTop: '16px', fontSize: '0.95rem' }}>
          <input
            type="checkbox"
            checked={localData.autoplay || false}
            onChange={(e) => updateLocalData('autoplay', e.target.checked)}
            style={{ marginRight: '8px' }}
          />
          Autoplay
        </label>
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save Video'}
        </button>
      </div>
    );
  };

  const FileBlockEditor = ({ block, saving, handleSaveBlock, handleFileUpload, labelStyle, inputStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    const handleFileInputChange = async (e, key) => {
      const file = e.target.files[0];
      if (!file) return;

      const url = await handleFileUpload(file);
      if (url) {
        updateLocalData(key, url);
        if (key === 'url' && !localData.filename) {
          updateLocalData('filename', file.name);
        }
      }
    };

    return (
      <div>
        <label style={labelStyle}>File Upload</label>
        <input
          type="file"
          onChange={(e) => handleFileInputChange(e, 'url')}
          style={inputStyle}
        />
        {localData.filename && (
          <div style={{ marginTop: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '4px' }}>
            <strong>File:</strong> {localData.filename}
          </div>
        )}
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save File'}
        </button>
      </div>
    );
  };

  const SectionBlockEditor = ({ block, saving, handleSaveBlock, labelStyle, inputStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    return (
      <div>
        <label style={labelStyle}>Section Title</label>
        <input
          type="text"
          value={localData.title || ''}
          onChange={(e) => updateLocalData('title', e.target.value)}
          style={inputStyle}
          placeholder="Enter section title"
        />
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save Section'}
        </button>
      </div>
    );
  };

  const ScormBlockEditor = ({ block }) => {
    const localData = block.data || {};

    return (
      <div style={{
        padding: '24px',
        background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
        borderRadius: '8px',
        marginBottom: '16px'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1a1a1a', fontSize: '1.2rem', fontWeight: '700' }}>
          SCORM Content Block (Read-Only)
        </h3>
        <div style={{ background: '#fff', padding: '16px', borderRadius: '4px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Package Title</div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#333' }}>
              {localData.package_title || 'N/A'}
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>SCO Title</div>
            <div style={{ fontSize: '1rem', fontWeight: '700', color: '#333' }}>
              {localData.sco_title || 'N/A'}
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>Launch Path</div>
            <div style={{ fontSize: '0.9rem', color: '#555', fontFamily: 'monospace' }}>
              {localData.launch_path || 'N/A'}
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '4px' }}>SCO Identifier</div>
            <div style={{ fontSize: '0.9rem', color: '#555', fontFamily: 'monospace' }}>
              {localData.sco_identifier || 'N/A'}
            </div>
          </div>
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#fff3cd',
            borderRadius: '4px',
            fontSize: '0.85rem',
            color: '#856404'
          }}>
            SCORM content blocks are imported from SCORM packages and cannot be edited directly.
          </div>
        </div>
      </div>
    );
  };

  const AudioBlockEditor = ({ block, saving, handleSaveBlock, handleFileUpload, labelStyle, inputStyle, textareaStyle, saveButtonStyle }) => {
    const [localData, setLocalData] = useState(block.data || {});

    const updateLocalData = (key, value) => {
      setLocalData(prev => ({ ...prev, [key]: value }));
    };

    const handleFileInputChange = async (e, key) => {
      const file = e.target.files[0];
      if (!file) return;

      const url = await handleFileUpload(file);
      if (url) {
        updateLocalData(key, url);
        if (key === 'url' && !localData.filename) {
          updateLocalData('filename', file.name);
        }
      }
    };

    return (
      <div>
        <label style={labelStyle}>Audio Upload</label>
        <input
          type="file"
          accept="audio/*"
          onChange={(e) => handleFileInputChange(e, 'url')}
          style={inputStyle}
        />
        {localData.url && (
          <audio controls style={{ width: '100%', marginTop: '8px' }}>
            <source src={localData.url} />
            Your browser does not support the audio element.
          </audio>
        )}
        <label style={{ ...labelStyle, marginTop: '16px' }}>Transcript</label>
        <textarea
          value={localData.transcript || ''}
          onChange={(e) => updateLocalData('transcript', e.target.value)}
          style={textareaStyle}
          rows={4}
          placeholder="Audio transcript for accessibility"
        />
        <button
          onClick={() => handleSaveBlock(block.id, localData)}
          disabled={saving[block.id]}
          style={saveButtonStyle}
        >
          {saving[block.id] ? 'Saving...' : 'Save Audio'}
        </button>
      </div>
    );
  };

  // ============================================================================
  // RENDER BLOCK EDITOR (NO HOOKS - ONLY SWITCHES AND RETURNS COMPONENTS)
  // ============================================================================

  const renderBlockEditor = (block) => {
    // All hooks are declared at the top level in each component above
    // This function only switches on block_type and returns the appropriate component
    switch (block.block_type) {
      case 'text':
        return (
          <TextBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            labelStyle={labelStyle}
            textareaStyle={textareaStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      case 'image':
        return (
          <ImageBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            handleFileUpload={handleFileUpload}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      case 'video':
        return (
          <VideoBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            selectStyle={selectStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      case 'file':
        return (
          <FileBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            handleFileUpload={handleFileUpload}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      case 'section':
        return (
          <SectionBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      case 'quiz':
        return <QuizEditor blockId={block.id} />;

      case 'assignment':
        return <AssignmentEditor blockId={block.id} />;

      case 'scorm':
        return <ScormBlockEditor block={block} />;

      case 'audio':
        return (
          <AudioBlockEditor
            block={block}
            saving={saving}
            handleSaveBlock={handleSaveBlock}
            handleFileUpload={handleFileUpload}
            labelStyle={labelStyle}
            inputStyle={inputStyle}
            textareaStyle={textareaStyle}
            saveButtonStyle={saveButtonStyle}
          />
        );

      default:
        return <div style={{ color: '#999' }}>Unknown block type: {block.block_type}</div>;
    }
  };

  // Styles
  const containerStyle = {
    padding: '24px',
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const toolbarStyle = {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '24px',
    padding: '16px',
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px'
  };

  const toolbarButtonStyle = {
    padding: '8px 16px',
    fontSize: '0.9rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const blockCardStyle = {
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '16px',
    position: 'relative'
  };

  const dragHandleStyle = {
    position: 'absolute',
    left: '-12px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '24px',
    height: '40px',
    background: '#d4af37',
    borderRadius: '4px',
    cursor: 'move',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    color: '#1a1a1a',
    fontWeight: 'bold'
  };

  const blockHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f0f0f0'
  };

  const blockTypeStyle = {
    fontSize: '0.85rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    color: '#d4af37',
    letterSpacing: '0.5px'
  };

  const deleteButtonStyle = {
    padding: '6px 12px',
    fontSize: '0.85rem',
    fontWeight: '600',
    background: '#e74c3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const labelStyle = {
    display: 'block',
    fontWeight: '600',
    color: '#1a1a1a',
    marginBottom: '8px',
    fontSize: '0.95rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '0.95rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    marginBottom: '16px'
  };

  const textareaStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '0.95rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    marginBottom: '16px',
    fontFamily: 'inherit',
    resize: 'vertical'
  };

  const selectStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '0.95rem',
    border: '2px solid #d4af37',
    borderRadius: '4px',
    marginBottom: '16px',
    background: '#fff'
  };

  const saveButtonStyle = {
    padding: '10px 20px',
    fontSize: '0.95rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginTop: '8px'
  };

  const emptyStateStyle = {
    padding: '48px 24px',
    textAlign: 'center',
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    color: '#666'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={emptyStateStyle}>Loading blocks...</div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1a1a1a', marginBottom: '24px' }}>
        Module Content Blocks
      </h2>

      {error && (
        <div style={{ padding: '12px 16px', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '4px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      <div style={toolbarStyle}>
        <button onClick={() => handleAddBlock('text')} style={toolbarButtonStyle}>
          + Add Text
        </button>
        <button onClick={() => handleAddBlock('image')} style={toolbarButtonStyle}>
          + Add Image
        </button>
        <button onClick={() => handleAddBlock('video')} style={toolbarButtonStyle}>
          + Add Video
        </button>
        <button onClick={() => handleAddBlock('file')} style={toolbarButtonStyle}>
          + Add File
        </button>
        <button onClick={() => handleAddBlock('section')} style={toolbarButtonStyle}>
          + Add Section
        </button>
        <button onClick={() => handleAddBlock('quiz')} style={toolbarButtonStyle}>
          + Add Quiz
        </button>
        <button onClick={() => handleAddBlock('audio')} style={toolbarButtonStyle}>
          + Add Audio
        </button>
      </div>

      {blocks.length === 0 ? (
        <div style={emptyStateStyle}>
          <p style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>No blocks yet</p>
          <p style={{ fontSize: '0.9rem', margin: '8px 0 0 0' }}>
            Add your first content block using the toolbar above
          </p>
        </div>
      ) : (
        <div>
          {blocks.map((block, index) => (
            <div
              key={block.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              style={{ ...blockCardStyle, position: 'relative', paddingLeft: '16px' }}
            >
              <div
                style={dragHandleStyle}
                title="Drag to reorder"
              >
                ::
              </div>

              <div style={blockHeaderStyle}>
                <span style={blockTypeStyle}>{block.block_type}</span>
                <button
                  onClick={() => handleDeleteBlock(block.id)}
                  style={deleteButtonStyle}
                >
                  Delete
                </button>
              </div>

              {renderBlockEditor(block)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ModuleBlockEditor;
